/**
 * Phase 8.6 — Canonical module manifest (frontend mirror).
 *
 * Source of truth: `/app/memory/MODULE_MANIFESTS.md` + backend
 * `/app/backend/modules/manifest.py`. This JS mirror is used at
 * render time by:
 *   • useModule(key)        — boolean module-ownership
 *   • useCapability(key)    — boolean capability resolution
 *   • <UpsellPill capability="..." />
 *   • Layout.jsx — pillar visibility derived from module ownership
 *
 * The mirror is kept manually in sync with the backend. The backend
 * /api/_modules/manifest endpoint is the canonical fetchable copy if
 * you need the full data (capabilities, headlines, prices) at
 * runtime. This file ships ONLY the lookup tables — the heavy
 * payload stays on the server.
 */

// ── Canonical module keys (9) ─────────────────────────────────────────
export const CANONICAL_MODULES = [
  "sales_flow",
  "ops",
  "teams",
  "projects",
  "stockroom",
  "bookkeep",
  "support",
  "connect",
  "cerebrum_ai",
];

// ── Pillar → module mapping (drives Sidebar pillar visibility) ────────
export const PILLAR_TO_MODULE = {
  sales:      "sales_flow",
  service:    "ops",
  team:       "teams",
  operations: "stockroom",
  projects:   "projects",
  finance:    "bookkeep",
  insights:   "sales_flow", // insights still renders dashboard from any module
  support:    "support",
  connect:    "connect",
};

// ── Module display metadata (used by UpsellPill + Sidebar labels) ─────
export const MODULE_META = {
  sales_flow:  { name: "Rogue SalesFlow",  short: "SalesFlow",  color: "#A78BFA", price: 15 },
  ops:         { name: "Rogue Ops",        short: "Ops",        color: "#22D3EE", price: 15 },
  teams:       { name: "Rogue Teams",      short: "Teams",      color: "#F472B6", price: 15 },
  projects:    { name: "Rogue Projects",   short: "Projects",   color: "#FBBF24", price: 15 },
  stockroom:   { name: "Rogue Stockroom",  short: "Stockroom",  color: "#34D399", price: 15 },
  bookkeep:    { name: "Rogue Bookkeep",   short: "Bookkeep",   color: "#10B981", price: 15 },
  support:     { name: "Rogue Support",    short: "Support",    color: "#FB7185", price: 15 },
  connect:     { name: "Rogue Connect",    short: "Connect",    color: "#60A5FA", price: 15 },
  cerebrum_ai: { name: "Cerebrum AI",      short: "Cerebrum",   color: "#C084FC", price: 39, isAddon: true },
};

// ── Capability → owning module (so a capability check is a module check) ─
// Each capability lives in exactly one module per the manifest.
// Cerebrum AI is special: ALL `ai_premium.*` capabilities require it.
//
// We DON'T enumerate every leaf capability in this file (cost of drift
// vs. backend). Instead the lookup falls back to module-key match: if
// `capability` starts with a canonical module key, that module gates it.
// `ai_premium.*` always gates on cerebrum_ai.
export function resolveCapabilityModule(capability) {
  if (!capability || typeof capability !== "string") return null;
  if (capability.startsWith("ai_premium.")) return "cerebrum_ai";
  // Explicit map for common cross-module surfaces
  const directMap = {
    "customer.activity_timeline.read":  "sales_flow",
    "customer.activity_timeline.write": "sales_flow",
    "customer.documents":               "sales_flow",
    "customer.contracts":               "sales_flow",
    "customer.followups":               "sales_flow",
    "customer.trips":                   "sales_flow",
    "customer.ticket_history":          "support",
    "customer.csat_score":              "support",
    "team_member.list.full":            "teams",
    "team_member.detail.full":          "teams",
    "team_member.performance.read":     "teams",
    "task.crud.full":                   "teams",
    "task.kanban.read":                 "teams",
    "task.kanban.write":                "teams",
    "task.dependencies":                "teams",
    "task.time_tracking":               "teams",
    "site.detail.full":                 "sales_flow",
    "equipment.installed_asset.crud":   "ops",
    "equipment.catalogue.crud":         "stockroom",
    "inventory.read":                   "stockroom",
    "ticket.crud":                      "support",
    "ticket.crud_lite":                 "ops",
    "invoice.outbound":                 "bookkeep",
    "invoice.payment_tracking":         "bookkeep",
    "profitability.per_customer":       "bookkeep",
    "profitability.per_project":        "bookkeep",
    "profitability.per_job":            "bookkeep",
    "portal.login":                     "connect",
    "portal.subdomain":                 "connect",
  };
  if (directMap[capability]) return directMap[capability];

  // Heuristic: prefix-match on module key. e.g. "jobcard.crud" doesn't
  // start with "ops" so we add domain hints below.
  const domainHints = {
    suspect:      "sales_flow",
    lead:         "sales_flow",
    sales_doc:    "sales_flow",
    trip:         "sales_flow",
    meeting:      "sales_flow",
    followup:     "sales_flow",
    call:         "sales_flow",
    account_application: "sales_flow",
    contracts:    "sales_flow",
    map:          "sales_flow",
    jobcard:      "ops",
    scheduler:    "ops",
    ticket:       "support",
    knowledge_base: "support",
    inventory:    "stockroom",
    supplier:     "stockroom",
    datasheet:    "stockroom",
    procurement:  "stockroom",
    order_request: "stockroom",
    expense:      "bookkeep",
    invoice:      "bookkeep",
    bill:         "bookkeep",
    cashflow:     "bookkeep",
    ar_aged_report: "bookkeep",
    ap_aged_report: "bookkeep",
    trial_balance_lite: "bookkeep",
    tax_codes:    "bookkeep",
    profitability: "bookkeep",
    project:      "projects",
    attendance:   "teams",
    leave:        "teams",
    performance:  "teams",
    recruitment:  "teams",
    chat:         "teams",
    contracts_employment: "teams",
    competitions: "teams",
    announcements: "teams",
    wellness_checkin: "teams",
    portal:       "connect",
  };
  const head = capability.split(".")[0];
  if (domainHints[head]) return domainHints[head];
  // Direct module-key prefix (rare)
  for (const mod of CANONICAL_MODULES) {
    if (capability === mod || capability.startsWith(mod + ".")) return mod;
  }
  return null;
}

// ── Bundle rules (mirrored from backend) ──────────────────────────────
export const BUNDLE_RULES = {
  bookkeep: {
    requiresAnyOf: ["sales_flow", "ops", "stockroom"],
    message: "Bookkeep needs SalesFlow, Ops or Stockroom to surface money over.",
  },
  connect: {
    requiresAnyOf: ["sales_flow", "ops", "support", "stockroom"],
    message: "Connect needs a customer-facing module (SalesFlow, Ops, Support or Stockroom).",
  },
  cerebrum_ai: {
    requiresAnyOf: CANONICAL_MODULES.filter((m) => m !== "cerebrum_ai"),
    message: "Cerebrum AI is an add-on — pick at least one module first.",
  },
};

export function validateBundleClient(modules) {
  const enabled = new Set(
    Object.entries(modules || {})
      .filter(([, v]) => v)
      .map(([k]) => k),
  );
  for (const [gatedKey, rule] of Object.entries(BUNDLE_RULES)) {
    if (!enabled.has(gatedKey)) continue;
    const overlap = rule.requiresAnyOf.some((req) => enabled.has(req));
    if (!overlap) return { ok: false, error: rule.message, blocker: gatedKey };
  }
  return { ok: true };
}
